import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';

import { useNavigate } from 'react-router';
import {
  LogoBadge,
  PlatformListMetadata,
  PlatformReport,
  PlatformReportSortBy,
  TableCard,
  useBaseAuthenticated,
} from 'shared/base';
import { LoadingTableRows } from 'shared/base/components/display/loadingTableRows';
import { TagsDisplay } from 'shared/base/components/display/tagsDisplay';

import { ReportActionMenu } from '../../controls/reportActionMenu';
import { parseReportYaml } from '../../utils';
import { getReportUrl } from '../../utils/navigation';

const headCells: { id: string; label: string; sortable?: boolean }[] = [
  { id: 'title', label: 'Name', sortable: true },
  { id: 'description', label: 'Description', sortable: true },
  { id: 'tags', label: 'Tags', sortable: false },
  { id: 'created_by', label: 'Created By', sortable: false },
  { id: 'updated_at', label: 'Last edited', sortable: true },
];

export const ReportsListingTable = ({
  handleChangeSort,
  isLoading,
  metadata = {
    page_size: 0,
    total_count: 0,
  },
  perPage,
  reports,
  setPerPage,
  setTablePage,
  sortBy,
  sortOrder,
  tablePage,
}: {
  handleChangeSort: (sortBy: any) => void;
  isError: boolean;
  isLoading: boolean;
  metadata: PlatformListMetadata;
  perPage: number;
  reports: PlatformReport[];
  setPerPage: (perPage: number) => void;
  setTablePage: (tablePage: number) => void;
  sortBy: PlatformReportSortBy;
  sortOrder: 'asc' | 'desc';
  tablePage: number;
}) => {
  const navigate = useNavigate();
  const { currentMembership } = useBaseAuthenticated();

  return (
    <Stack gap={2} sx={{ overflowY: 'auto' }}>
      <TableCard
        count={metadata.total_count}
        hidePaginationWhenSingle={true}
        onPageChange={(page: number) => setTablePage(page)}
        onPerPageChange={setPerPage}
        page={tablePage}
        perPage={perPage}
      >
        <Table sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  sortDirection={sortBy === headCell.id ? sortOrder : false}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={sortBy === headCell.id}
                      direction={sortBy === headCell.id ? sortOrder : 'asc'}
                      onClick={() => handleChangeSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    <>{headCell.label}</>
                  )}
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <LoadingTableRows columnCount={headCells.length} />
            ) : (
              reports?.map((report: any) => {
                const { content: yaml, created_by, description, title, updated_at } = report;
                const reportData = parseReportYaml(yaml);
                const { tags } = reportData;

                return (
                  <TableRow
                    key={report.id}
                    hover={true}
                    onClick={() => navigate(getReportUrl(report.id, currentMembership.team.name))}
                  >
                    <TableCell>
                      <Stack alignItems="center" direction="row" spacing={1}>
                        <LogoBadge
                          backgroundColor="transparent"
                          height={20}
                          imgHeight={20}
                          imgWidth={20}
                          src={
                            reportData.logo ? `/images/report_icons/${reportData.logo}.webp` : ''
                          }
                          width={20}
                        />
                        <Typography variant="body2">{title}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 350,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {description}
                    </TableCell>
                    <TableCell>
                      <TagsDisplay limit={2} showKeyValue={false} tags={tags} />
                    </TableCell>
                    <TableCell>{created_by.name}</TableCell>
                    <TableCell>
                      {updated_at
                        ? new Date(updated_at).toLocaleDateString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })
                        : ''}
                    </TableCell>
                    <TableCell
                      onClick={(event) => event.stopPropagation()}
                      sx={{ textAlign: 'right' }}
                    >
                      <ReportActionMenu id={report.id} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableCard>
    </Stack>
  );
};
